import {
  Container,
  Grid2 as Grid,
  Grid2Props as GridProps,
} from "@mui/material";

import "./styles.scss";
import { useContext, useEffect, useState } from "react";

import MegaLoader from "../../Components/MegaLoader";
import DisplayAlert from "../../Components/DisplayAlert";
import {
  Button,
  Card,
  CopyButton,
  Modal,
  Pagination,
  Paper,
  Table,
  TableThProps,
  Tabs,
  Text,
  TextInput,
  TextProps,
  Tooltip,
} from "@mantine/core";
import { data, Link, useParams } from "react-router-dom";
import {
  BrandImages,
  Colors,
  ValidMinimumCouponCodeLength,
} from "../../Lib/Data";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  PaginationProps,
  PayoutRequestProps,
  PromoterLeaderboardRecordProps,
  PromoterProps,
  PromoterTransactionProps,
  UserProfileProps,
} from "../../Lib/Types";
import { Endpoints } from "../../Lib/Endpoints";
import {
  IconCurrencyNaira,
  IconEdit,
  IconHandStop,
  IconReceipt,
  IconTimeline,
  IconUser,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getFinancialValueFromNumeric,
  getOriginalValueFromDiscount,
  getPaginationTotal,
} from "../../Lib/Methods";
import { useDisclosure } from "@mantine/hooks";
import { getValueFromValueOptions } from "@mui/x-data-grid/components/panel/filterPanel/filterPanelUtils";
import { AppContext } from "../Dashboard";
import { useForm } from "@mantine/form";

const SinglePromoter: React.FC = () => {
  const context = useContext(AppContext);
  const [theme, setTheme] = useState<"dark" | "light">("light");
  const { userID } = useParams();

  const [transactionsPagination, setTransactionsPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const [promotersPagination, setPromotersPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const [payoutsPagination, setPayoutsPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: promoter,
    isLoading: isLoadingPromoter,
    reloadData: reloadPromoterProfile,
  } = usePerformRequest<
    NonPaginatedResponse<{ promoter: PromoterProps; user: UserProfileProps }>
  >({
    method: "GET",
    route: Endpoints.GetSinglePromoter(userID as string),
  });
  const {
    data: payouts,
    isLoading: isLoadingPayouts,
    reloadData: reloadPayouts,
  } = usePerformRequest<PaginatedResponse<PayoutRequestProps[]>>({
    method: "POST",
    body: {
      status: "all",
    },
    route: Endpoints.GetSinglePromoterPayoutHistory(
      payoutsPagination.page,
      payoutsPagination.limit,
      userID as string
    ),
  });
  const {
    data: leaderboard,
    isLoading: isLoadingLeaderboard,
    reloadData: reloadLeaderboard,
  } = usePerformRequest<PaginatedResponse<PromoterLeaderboardRecordProps[]>>({
    method: "GET",
    route: Endpoints.GetPromotersLeaderboard(
      promotersPagination.page,
      promotersPagination.limit
    ),
  });
  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    reloadData: reloadTransactions,
  } = usePerformRequest<PaginatedResponse<PromoterTransactionProps[]>>({
    method: "GET",
    route: Endpoints.GetSinglePromoterTransactions(
      transactionsPagination.page,
      transactionsPagination.limit,
      userID as string
    ),
  });
  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);
  const [isEditModalOpened, { open: OpenEditModal, close: CloseEditModal }] =
    useDisclosure(false);

  const promoterProfileForm = useForm({
    initialValues: {
      coupon: "",
      accountNumber: "",
    },
    validate: {
      coupon: (value) =>
        value && value.length >= ValidMinimumCouponCodeLength
          ? null
          : `Minimum ${ValidMinimumCouponCodeLength} characters`,
      accountNumber: (value) =>
        value && value.length === 10 ? null : `10 characters`,
    },
  });

  const [isUpdatingPromoter, setUpdatingPromoter] = useState<boolean>(false);
  const UpdatePromoter = async () => {
    const { hasErrors } = promoterProfileForm.validate();
    if (!hasErrors) {
      setUpdatingPromoter(true);
      const { coupon, accountNumber } = promoterProfileForm.values;
      const r = await PerformRequest({
        method: "PUT",
        route: Endpoints.GetUpdatePromoterProfile(
          promoter?.data?.promoter?.id as string
        ),
        data: {
          coupon: coupon,
          accountNumber: accountNumber,
        },
        callback: () => {
          setUpdatingPromoter(false);
        },
      });
      setUpdatingPromoter(false);
      if (r) {
        if (r.status === 200) {
          reloadPromoterProfile();
          DefaultSuccessNotification("Promoter updated successfully!");
          CloseEditModal();
          promoterProfileForm.reset();
        }
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    }
  };

  useEffect(() => {
    if (
      promoter &&
      promoter.data &&
      promoter.data.promoter &&
      promoter.data.promoter.id
    ) {
      promoterProfileForm.setValues({
        coupon: promoter.data.promoter.coupon,
        accountNumber: promoter.data.promoter.accountNumber,
      });
    }
  }, [promoter]);

  const [currentTransaction, setCurrentTransaction] =
    useState<PromoterTransactionProps>();

  const [isUpdatingPayout, setUpdatingPayout] = useState<boolean>(false);

  const MakePayout = async () => {
    setUpdatingPayout(true);
    const r = await PerformRequest({
      method: "PATCH",
      route: Endpoints.GetMakePayout(promoter?.data?.promoter?.id as string),
      callback: () => {
        setUpdatingPayout(false);
      },
    });
    setUpdatingPayout(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Payout made successfully!");
        reloadPayouts();
      } else if (r.status === 401) {
        DefaultErrorNotification("Insufficient balance!");
      } else {
        DefaultErrorNotification("An error occurred! Please try again");
      }
    } else {
      DefaultErrorNotification("An error occurred! Please try again");
    }
  };

  const gridItemProps: GridProps = {
    size: {
      xl: 6,
      lg: 6,
      md: 12,
      sm: 12,
      xs: 12,
    },
  };
  const profileCardLabelProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.WhiteBlueTransparent : Colors.GraySecondary,
    fw: 500,
  };
  const profileCardValueProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.GreenPrimary : Colors.GreenSecondary,
    fw: 500,
    ml: 5,
  };
  const tabTitleProps: TextProps = {
    fz: "md",
    c: theme === "dark" ? Colors.WhiteBlueTransparent : Colors.GreenSecondary,
    fw: 500,
    mb: 10,
  };
  const tableRowHeadProps: TableThProps = {
    style: {
      backgroundColor: theme === "dark" ? "transparent" : Colors.White,
      color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
      borderRight: "1px solid #E0E0E0",
    },
  };
  return (
    <Container maxWidth="lg">
      {context ? (
        <>
          {isLoadingPromoter ? (
            <MegaLoader />
          ) : (
            <>
              {promoter?.data?.promoter?.active ? (
                <div className="promoter-container">
                  <br />
                  <Tabs
                    variant={theme === "dark" ? "outline" : "default"}
                    defaultValue="profile"
                    color={Colors.GreenPrimary}
                  >
                    <Tabs.List grow mb={20}>
                      <Tabs.Tab value="profile">
                        <IconUser
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="transactions">
                        <IconReceipt
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="payouts">
                        <IconCurrencyNaira
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="leaderboard">
                        <IconTimeline
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="profile">
                      <Text {...tabTitleProps}>Promoter Details</Text>

                      {isLoadingPromoter ? (
                        <MegaLoader />
                      ) : (
                        <>
                          <Card
                            maw={470}
                            withBorder
                            className={`profile-card profile-card-${theme}`}
                          >
                            <div className="flex-row align-center justify-between">
                              <div className="initials flex-row align-center justify-center">
                                <CopyButton value={promoter?.data?.user?.id}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied ? "Copied!" : "Copy to clipboard"
                                      }
                                    >
                                      <Text className="text" onClick={copy}>
                                        {promoter?.data?.user?.name[0]}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </div>
                              <Button
                                color={Colors.GreenPrimary}
                                leftSection={<IconEdit size={18} />}
                                onClick={OpenEditModal}
                              >
                                Edit
                              </Button>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>
                                Promoter ID:
                              </Text>

                              <CopyButton value={promoter?.data?.promoter?.id}>
                                {({ copied, copy }) => (
                                  <Tooltip
                                    label={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <Text
                                      {...profileCardValueProps}
                                      className="pointer"
                                      onClick={copy}
                                    >
                                      {promoter?.data?.promoter?.id}
                                    </Text>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>User ID:</Text>

                              <Text {...profileCardValueProps}>
                                {promoter?.data?.user?.id}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>Name:</Text>

                              <Text {...profileCardValueProps}>
                                {promoter?.data?.user?.name}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>Email:</Text>

                              <Text {...profileCardValueProps}>
                                {promoter?.data?.user?.email}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>
                                Promotion Date:
                              </Text>

                              <Text {...profileCardValueProps}>
                                {dayjs(
                                  promoter?.data?.promoter?.createdAt ?? ""
                                ).format("ddd, DD MMM YYYY")}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>Coupon:</Text>

                              <CopyButton
                                value={promoter?.data?.promoter?.coupon}
                              >
                                {({ copied, copy }) => (
                                  <Tooltip
                                    label={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <Text
                                      {...profileCardValueProps}
                                      className="pointer"
                                      onClick={copy}
                                    >
                                      {promoter?.data?.promoter?.coupon}
                                    </Text>
                                  </Tooltip>
                                )}
                              </CopyButton>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>Status:</Text>

                              <Text
                                fz="sm"
                                ml={5}
                                fw={600}
                                c={
                                  promoter?.data?.promoter?.active
                                    ? Colors.GreenSecondary
                                    : Colors.RedPrimary
                                }
                              >
                                {promoter?.data?.promoter?.active
                                  ? "Active"
                                  : "Inactive"}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>
                                Lifetime Earnings:
                              </Text>

                              <Text {...profileCardValueProps}>
                                ₦
                                {getFinancialValueFromNumeric(
                                  promoter?.data?.promoter?.totalEarnings
                                )}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>
                                Current Balance:
                              </Text>

                              <Text {...profileCardValueProps}>
                                ₦
                                {getFinancialValueFromNumeric(
                                  promoter?.data?.promoter?.balance
                                )}
                              </Text>
                            </div>
                            <div className="row flex-row align-center">
                              <Text {...profileCardLabelProps}>
                                Bank Account:
                              </Text>

                              <Text {...profileCardValueProps}>
                                {promoter?.data?.promoter?.accountNumber ?? "-"}
                              </Text>
                            </div>
                          </Card>
                          <Modal
                            opened={isEditModalOpened}
                            onClose={CloseEditModal}
                            centered
                            title="Update Promoter Information"
                          >
                            <TextInput
                              label="Account Number"
                              placeholder="Enter Account Number (OPAY)"
                              {...promoterProfileForm.getInputProps(
                                "accountNumber"
                              )}
                            />
                            <TextInput
                              mt={10}
                              label="Coupon"
                              placeholder="Enter Coupon Code"
                              {...promoterProfileForm.getInputProps("coupon")}
                            />
                            <Button
                              w={150}
                              onClick={UpdatePromoter}
                              loading={isUpdatingPromoter}
                              color={Colors.GreenPrimary}
                              mt={15}
                            >
                              Update
                            </Button>
                          </Modal>
                        </>
                      )}
                    </Tabs.Panel>
                    <Tabs.Panel value="transactions">
                      <Text {...tabTitleProps}>Transactions</Text>
                      {currentTransaction && (
                        <Modal
                          opened={isTransactionModalOpened}
                          onClose={() => {
                            setCurrentTransaction(undefined);
                            CloseTransactionModal();
                          }}
                          title="Transaction Details"
                          centered
                          size="lg"
                          styles={{
                            body: {
                              padding: 0,
                            },
                            content: {
                              borderRadius: "5px",
                            },
                            header: {
                              backgroundColor:
                                theme === "dark"
                                  ? "var(--mantine-color-dark-7)"
                                  : Colors.White,
                              color:
                                theme === "dark"
                                  ? Colors.White
                                  : Colors.GreenSecondary,
                            },
                          }}
                        >
                          <div
                            className={`transaction-modal transaction-modal-${theme}`}
                          >
                            <Table
                              variant="vertical"
                              layout="fixed"
                              withTableBorder
                            >
                              <Table.Thead>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps} w={140}>
                                    Code
                                  </Table.Th>
                                  <Table.Td>
                                    <CopyButton
                                      value={currentTransaction.coupon.code}
                                    >
                                      {({ copied, copy }) => (
                                        <Tooltip
                                          label={
                                            copied
                                              ? "Copied!"
                                              : "Copy to clipboard"
                                          }
                                        >
                                          <div
                                            className="pointer flex-row align-center"
                                            onClick={copy}
                                            style={{
                                              color: copied
                                                ? Colors.GreenPrimary
                                                : Colors.BluePrimary,
                                              height: "100%",
                                            }}
                                          >
                                            {currentTransaction.coupon.code}
                                          </div>
                                        </Tooltip>
                                      )}
                                    </CopyButton>
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Discount
                                  </Table.Th>
                                  <Table.Td
                                    c={
                                      theme === "dark"
                                        ? Colors.White
                                        : Colors.GraySecondary
                                    }
                                  >
                                    {currentTransaction.coupon.discount}%
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Date
                                  </Table.Th>
                                  <Table.Td
                                    c={
                                      theme === "dark"
                                        ? Colors.White
                                        : Colors.BlackPrimary
                                    }
                                  >
                                    {dayjs(currentTransaction.createdAt).format(
                                      "DD MMM YYYY - hh:mmA"
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Earnings
                                  </Table.Th>
                                  <Table.Td
                                    fw={600}
                                    c={Colors.GreenPrimary}
                                    fz={17}
                                  >
                                    ₦
                                    {getFinancialValueFromNumeric(
                                      getOriginalValueFromDiscount(
                                        currentTransaction.amount,
                                        currentTransaction.coupon.discount
                                      )
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Service(s)
                                  </Table.Th>
                                  <Table.Td>
                                    <div className="flex-row align-center temp-service-images">
                                      {currentTransaction.services.map((s) => (
                                        <img
                                          src={BrandImages.retrieveImage(s)}
                                          alt=""
                                          className="transaction-image-row"
                                        />
                                      ))}
                                    </div>
                                  </Table.Td>
                                </Table.Tr>
                              </Table.Thead>
                            </Table>
                            <Button
                              color={Colors.GreenPrimary}
                              onClick={() => {
                                CloseTransactionModal();
                                setCurrentTransaction(undefined);
                              }}
                              w={120}
                              mt={20}
                            >
                              Close
                            </Button>
                          </div>
                        </Modal>
                      )}
                      <Grid container spacing={4}>
                        {transactions?.data.map((transaction) => {
                          return (
                            <Grid {...gridItemProps} key={transaction.id}>
                              <Paper
                                onClick={() => {
                                  setCurrentTransaction(transaction);
                                  OpenTransactionModal();
                                }}
                                withBorder
                                className={`transaction transaction-${theme} pointer`}
                              >
                                <div className="content flex-col justify-between width-100">
                                  <div className="flex-row align-center justify-between width-100">
                                    <div className="flex-col">
                                      <Text
                                        c={
                                          theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.GrayPrimary
                                        }
                                        mb={-10}
                                        fz={14}
                                      >
                                        Discount Value
                                      </Text>
                                      <Text
                                        fw={500}
                                        fz={19}
                                        w="fit-content"
                                        c={Colors.BluePrimary}
                                      >
                                        {transaction.coupon.discount}%
                                      </Text>
                                    </div>
                                    <div className="flex-col align-end">
                                      <Text
                                        c={
                                          theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.GraySecondary
                                        }
                                        mb={0}
                                        fz={14}
                                      >
                                        {dayjs(transaction.createdAt).format(
                                          "ddd, DD MMM YYYY"
                                        )}
                                      </Text>
                                      <Text c={Colors.GrayPrimary} fz={14}>
                                        <b>at</b>&nbsp;
                                        {dayjs(transaction.createdAt).format(
                                          "hh:mm A"
                                        )}
                                      </Text>
                                    </div>
                                  </div>
                                  <div className="flex-row align-center width-100 justify-between">
                                    <Text
                                      fw={500}
                                      c={Colors.GreenPrimary}
                                      fz={20}
                                    >
                                      ₦
                                      {getFinancialValueFromNumeric(
                                        getOriginalValueFromDiscount(
                                          transaction.amount,
                                          transaction.coupon.discount
                                        )
                                      )}
                                    </Text>
                                    <div className="flex-row align-center">
                                      {transaction.services.map((s, index) => (
                                        <img
                                          key={index}
                                          src={BrandImages.retrieveImage(s)}
                                          alt=""
                                          className="transaction-image-row"
                                        />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                      <div className="flex-row align-center justify-center width-100">
                        <Pagination
                          value={transactionsPagination.page}
                          onChange={(value) => {
                            setTransactionsPagination({
                              ...transactionsPagination,
                              page: value,
                            });
                          }}
                          my={40}
                          disabled={isLoadingTransactions}
                          total={getPaginationTotal(
                            transactions?.totalRecords,
                            transactionsPagination.limit
                          )}
                        />
                      </div>
                    </Tabs.Panel>
                    <Tabs.Panel value="payouts">
                      <div className="flex-row align-center justify-between width-100">
                        <Text {...tabTitleProps}>Payout History</Text>
                        <Button
                          title={
                            promoter?.data?.promoter?.balance <= 0
                              ? "Insufficient balance"
                              : ""
                          }
                          color={Colors.GreenPrimary}
                          mb={10}
                          disabled={promoter?.data?.promoter?.balance <= 0}
                          onClick={MakePayout}
                          loading={isUpdatingPayout}
                        >
                          Make Payout
                        </Button>
                      </div>
                      {isLoadingPayouts ? (
                        <MegaLoader />
                      ) : (
                        <>
                          {payouts && payouts.data && (
                            <Grid container spacing={4}>
                              {payouts?.data.map((payout) => {
                                return (
                                  <Grid {...gridItemProps} key={payout.id}>
                                    <Paper
                                      withBorder
                                      className={`transaction transaction-${theme}`}
                                    >
                                      <div className="content flex-col justify-between width-100">
                                        <div className="flex-row align-center justify-between width-100">
                                          <div className="flex-col">
                                            <Text
                                              c={Colors.GrayPrimary}
                                              mb={-10}
                                              fz={14}
                                            >
                                              Status
                                            </Text>
                                            <Text
                                              fw={600}
                                              fz={15}
                                              mt={4}
                                              tt="uppercase"
                                              w="fit-content"
                                              c={
                                                payout.status === "pending"
                                                  ? Colors.OrangePrimary
                                                  : Colors.GreenPrimary
                                              }
                                            >
                                              {payout.status}
                                            </Text>
                                          </div>
                                          <div className="flex-col align-end">
                                            <Text
                                              c={Colors.GrayPrimary}
                                              mb={-10}
                                              fz={14}
                                            >
                                              Payout Date
                                            </Text>
                                            <Text
                                              mt={5}
                                              c={
                                                theme === "dark"
                                                  ? Colors.WhiteBlueTransparent
                                                  : Colors.GraySecondary
                                              }
                                              fz={14}
                                            >
                                              {dayjs(payout.createdAt).format(
                                                "ddd, DD MMM YYYY"
                                              )}
                                            </Text>
                                          </div>
                                        </div>
                                        <div className="flex-row align-center width-100 justify-between">
                                          <Text
                                            fw={500}
                                            c={Colors.GreenPrimary}
                                            fz={20}
                                          >
                                            ₦
                                            {getFinancialValueFromNumeric(
                                              payout.amount
                                            )}
                                          </Text>
                                          <div className="flex-row align-center"></div>
                                        </div>
                                      </div>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </>
                      )}
                    </Tabs.Panel>
                    <Tabs.Panel value="leaderboard">
                      <Text {...tabTitleProps}>Promoters Leaderboard</Text>
                      <Table
                        borderColor={
                          theme === "dark" ? Colors.White : Colors.GrayTertiary
                        }
                      >
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Rank
                            </Table.Th>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Promoter
                            </Table.Th>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Earnings
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {isLoadingLeaderboard ? (
                            <div className="flex-row align-center justify-center">
                              <MegaLoader />
                            </div>
                          ) : (
                            <>
                              {leaderboard && leaderboard.data && (
                                <>
                                  {leaderboard.data.map((p, index) => {
                                    return (
                                      <Table.Tr
                                        key={p._id}
                                        bg={
                                          promoter?.data?.promoter?.email ===
                                          p.email
                                            ? Colors.GreenLight
                                            : theme === "dark"
                                            ? Colors.BlackPrimary
                                            : Colors.White
                                        }
                                        c={
                                          context?.profile?.email === p.email
                                            ? Colors.BlackPrimary
                                            : theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.BlackPrimary
                                        }
                                      >
                                        <Table.Td>{index + 1}</Table.Td>
                                        <Table.Td>{p.email}</Table.Td>
                                        <Table.Td
                                          c={Colors.GreenSecondary}
                                          fw={500}
                                        >
                                          ₦
                                          {getFinancialValueFromNumeric(
                                            p.totalEarnings
                                          )}
                                        </Table.Td>
                                      </Table.Tr>
                                    );
                                  })}
                                </>
                              )}
                            </>
                          )}
                        </Table.Tbody>
                      </Table>
                      <div className="flex-row align-center justify-center width-100">
                        <Pagination
                          value={promotersPagination.page}
                          onChange={(value) => {
                            setPromotersPagination({
                              ...promotersPagination,
                              page: value,
                            });
                          }}
                          my={40}
                          disabled={isLoadingTransactions}
                          total={getPaginationTotal(
                            transactions?.totalRecords,
                            promotersPagination.limit
                          )}
                        />
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </div>
              ) : (
                <DisplayAlert
                  title="Account is not a promoter"
                  children={
                    <div className="flex-col">
                      <Text>
                        This user account has not been promoted or does not
                        exist!
                      </Text>
                      <Link to="/d/services">
                        <Button mt={10} color={Colors.GreenPrimary}>
                          Back to Promoters
                        </Button>
                      </Link>
                    </div>
                  }
                />
              )}
            </>
          )}
        </>
      ) : (
        <MegaLoader />
      )}
    </Container>
  );
};

export default SinglePromoter;
